/**
 * LoginPage — Final Production Design
 * ─────────────────────────────────────────────────────────
 * • NO panel backgrounds / borders — single-page feel
 * • Only LOGIN CARD is highlighted (green glow)
 * • Responsive: mobile (center only) → tablet (center+right) → desktop (all 3)
 * • 50% reduced gaps throughout
 * • Real demo logins from webapp.clientmaster
 * ─────────────────────────────────────────────────────────
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Eye, EyeOff, User, Lock, ArrowRight,
  Mic, Fingerprint, Scan, Shield, Zap, Globe,
  BadgeCheck, Building2, Cpu, Globe2,
  AlertTriangle, CheckSquare, MessageCircle,
  Bell, Activity, Lightbulb, Quote, Calendar,
  Star, CheckCircle, Package,
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

import { Button }    from '../../components/ui/button';
import { Input }     from '../../components/ui/input';
import { Checkbox }  from '../../components/ui/checkbox';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Badge }     from '../../components/ui/badge';
import { Separator } from '../../components/ui/separator';

import { useAuth }          from '../context/AuthContext';
import { useTheme }         from '../context/ThemeContext';
import { useGlobalization } from '../context/GlobalizationContext';
import RetailCliqLogo       from '../components/RetailCliqLogo';
import ThemeToggle          from '../components/ThemeToggle';
import FloatingParticles    from '../components/FloatingParticles';
import ForgotPasswordModal  from '../components/ForgotPasswordModal';
import VoiceLoginPanel      from '../components/VoiceLoginPanel';
import WeatherWidget        from '../components/WeatherWidget';
// CompanySelector import removed — feature hidden per product decision.
import { getTodayThought, getTodayIdea } from '../data/thoughts';
import useRecentCompanies   from '../hooks/useRecentCompanies';
import { FormHelp } from '../../components/help-engine/FormHelp';
import { API_BASE, API } from '../../lib/api';
import { LanguageSwitcher } from '../../lib/manchala-i18n.react.js';

const P_COLOR = { high: '#FF5A6A', medium: '#F5C451', normal: '#3DD56D', low: '#7A7A7A' };
const CAT_ICON = { task: CheckSquare, alert: AlertTriangle, message: MessageCircle, feed: Bell };
const CAT_META = {
  license:      '#3DD56D',
  news:         '#4CC3FF',
  notification: '#F5C451',
  feed:         '#A78BFA',
  compliance:   '#FF5A6A',
};

/* ── Tiny section label ─────────────────────────────────────────────────────── */
function SL({ icon: Icon, text, color = '#3DD56D' }) {
  return (
    <div className="flex items-center gap-1 mb-1.5">
      {Icon && <Icon size={10} style={{ color }} />}
      <span className="text-[10px] font-semibold dark:text-neutral-500 text-gray-400 uppercase tracking-[0.14em]">
        {text}
      </span>
    </div>
  );
}

/* ── Insight row ────────────────────────────────────────────────────────────── */
function InsightRow({ item }) {
  const dotColor = P_COLOR[item.priority] || '#3DD56D';
  return (
    <div className="flex items-start gap-1.5 py-0.5">
      <div className="w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0" style={{ backgroundColor: dotColor }} />
      <div className="min-w-0 flex-1">
        <p className="dark:text-neutral-200 text-gray-700 text-[11px] font-medium leading-snug truncate">{item.title}</p>
        {item.body && <p className="dark:text-neutral-500 text-gray-400 text-[10px] mt-0.5 rcq-line-clamp-1">{item.body}</p>}
        {(item.module || item.due_date) && (
          <p className="dark:text-neutral-600 text-gray-400 text-[10px]">
            {item.module && <span>{item.module}</span>}
            {item.due_date && <span className="ml-1">· Due: {item.due_date}</span>}
          </p>
        )}
      </div>
    </div>
  );
}

export default function LoginPage() {
  const { loginWithTemp, completeLogin, accessToken } = useAuth();
  const { isDark } = useTheme();
  const { t, formatTime, formatDate } = useGlobalization();
  const navigate = useNavigate();

  const [username,     setUsername]     = useState('');
  const [subUsername,  setSubUsername]  = useState('');  // child user login
  const [password,     setPassword]     = useState('');
  const [showPwd,      setShowPwd]      = useState(false);
  const [rememberMe,   setRememberMe]   = useState(false);
  const [loading,      setLoading]      = useState(false);
  const [authTab,      setAuthTab]      = useState('password');
  const [showForgot,   setShowForgot]   = useState(false);
  const [lockoutMsg,   setLockoutMsg]   = useState(null);
  const [expiryWarn,   setExpiryWarn]   = useState(null);
  const [geoCoords,    setGeoCoords]    = useState(null);

  // Company selector state — CompanySelector dropdown is HIDDEN per product decision.
  // We still keep `recent` for the Quick-Login chips below the password form.
  const { recent, addRecent } = useRecentCompanies();

  const [compInsights, setCompInsights] = useState([]);
  const [userInsights, setUserInsights] = useState([]);
  const [activity,     setActivity]     = useState([]);
  const [time,         setTime]         = useState(new Date());

  const todayThought = getTodayThought();
  const todayIdea    = getTodayIdea();

  useEffect(() => { if (accessToken) navigate('/select-app', { replace: true }); }, [accessToken, navigate]);

  // Note: do NOT auto-fill the username with the last-used client_id.
  // The username field must be the operator's username, never the ClientId.
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        pos => setGeoCoords({ latitude: pos.coords.latitude, longitude: pos.coords.longitude }),
        () => {},
        { timeout: 5000, maximumAge: 600000 }
      );
    }
  }, []);

  // Fetch company insights (left panel)
  useEffect(() => {
    axios.get(`${API}/insights/company?limit=4`)
      .then(r => setCompInsights(r.data.insights || []))
      .catch(() => setCompInsights([
        { id:1, category:'license',      title:'Enterprise License Active',  body:'Valid until Dec 2025.', priority:'normal' },
        { id:2, category:'news',         title:'POS v3.2 Available',         body:'Faster checkout + UPI.', priority:'normal' },
        { id:3, category:'notification', title:'Maintenance Sunday 2 AM',    body:'~30 min downtime.',     priority:'high' },
        { id:4, category:'feed',         title:'AI Forecasting Live',         body:'Now in Analytics.',     priority:'normal' },
      ]));
  }, []);

  // Fetch user insights (right panel)
  useEffect(() => {
    const sv = localStorage.getItem('rcq_user') || sessionStorage.getItem('rcq_user');
    const uid = sv ? (JSON.parse(sv).id || 'u001') : 'u001';
    axios.get(`${API}/insights/user/${uid}?limit=8`)
      .then(r => setUserInsights(r.data.insights || []))
      .catch(() => setUserInsights([
        { id:1, category:'task',   title:'GST Filing Due',           body:'GSTR-3B due on 20th.',         priority:'high',   module:'Finance',   due_date:'2025-04-20' },
        { id:2, category:'task',   title:'Inventory Audit',           body:'Monthly audit scheduled.',    priority:'medium', module:'Inventory', due_date:'2025-04-25' },
        { id:3, category:'alert',  title:'Low Stock: 18 SKUs',        body:'Below reorder level.',        priority:'high',   module:'Inventory' },
        { id:4, category:'alert',  title:'5 Invoices Pending',        body:'Awaiting your approval.',     priority:'medium', module:'Finance' },
        { id:5, category:'message','title':"Chairman's Message",      body:'Achieve 10% growth.',         priority:'normal', module:'General' },
      ]));
  }, []);

  // Fetch activity
  useEffect(() => {
    axios.get(`${API}/user/activity`)
      .then(r => setActivity((r.data.activity || []).slice(0, 3)))
      .catch(() => setActivity([
        { action:'Invoice #2891 Approved', detail:'Finance',   time:'Today 09:12 AM' },
        { action:'Stock adjusted 45 SKUs', detail:'Inventory', time:'Yesterday 06:30 PM' },
        { action:'New Employee Onboarded', detail:'HR',        time:'Yesterday 03:15 PM' },
      ]));
  }, []);

  // Demo-logins fetch removed — Quick Login is now driven by `recent` (previous successful logins).

  // Clock
  useEffect(() => {
    const id = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  const handleLogin = useCallback(async (e) => {
    e.preventDefault();
    if (!username.trim() || !password.trim()) { toast.error('Please enter credentials'); return; }
    setLockoutMsg(null); setExpiryWarn(null);
    setLoading(true);
    try {
      const payload = {
        username: username.trim(),
        password,
        remember_me: rememberMe,
        geo: geoCoords || undefined,
      };
      // Support child user: if subUsername provided, send as sub_username
      if (subUsername.trim()) payload.sub_username = subUsername.trim();

      const { data } = await axios.post(`${API}/auth/login`, payload);

      // 21 CFR: forced password change
      if (data.requiresPasswordChange) {
        completeLogin(data.access_token, data.user_summary, rememberMe);
        toast.warning(data.changeReason === 'expired' ? 'Your password has expired. Please set a new password.' : 'Please set a new password before continuing.');
        navigate('/change-password', { state: { reason: data.changeReason } });
        return;
      }

      // 21 CFR: expiry warning
      if (data.warning?.expiresInDays !== undefined) {
        setExpiryWarn(data.warning);
        toast.warning(`Password expires in ${data.warning.expiresInDays} day(s). Please change it soon.`, { duration: 8000 });
      }

      if (data.requiresMfa) {
        // MFA required — redirect to OTP verification page (no Welcome toast yet)
        loginWithTemp(data.temp_token, data.user_summary, rememberMe, data.email_hint);
        navigate('/mfa');
      } else {
        // Direct login (demo user or no email) — show Welcome and complete login
        toast.success(`Welcome, ${data.user_summary?.name || username}!`);
        // Persist full globalContext for the main ERP app to consume
        try {
          const storage = rememberMe ? localStorage : sessionStorage;
          if (data.globalContext) storage.setItem('rcq_global_context', JSON.stringify(data.globalContext));
        } catch (_) {}

        // ─── Record this successful login for the Quick-Login chips ────────
        try {
          const cid = data.globalContext?.clientId || data.user_summary?.client_id || data.user_summary?.slno;
          if (cid) {
            addRecent({
              client_id: cid,
              company:   data.globalContext?.company?.compName || data.user_summary?.company || '',
              name:      data.user_summary?.name || '',
              user_name: username.trim(),
            });
          }
        } catch (_) { /* silent */ }

        // ─── License check (non-blocking) ──────────────────────────────────
        try {
          const clientId = data.globalContext?.clientId || data.user_summary?.client_id || data.user_summary?.slno || '';
          if (clientId) {
            const licRes = await fetch(`${API_BASE}/api/license/status?clientId=${encodeURIComponent(clientId)}`);
            const licData = await licRes.json();
            if (licData.success && (!licData.licensed || licData.status === 'Expired')) {
              toast.error('⚠️ License Expired — Contact RetailCLIQ support at support@retailcliq.com to renew.', { duration: 10000 });
            } else if (licData.success && licData.status === 'Trial' && licData.daysLeft !== null && licData.daysLeft <= 10) {
              toast.warning(`Trial license expiring in ${licData.daysLeft} day(s). Contact RetailCLIQ support to upgrade.`, { duration: 8000 });
            }
          }
        } catch (_) { /* silent — don't block login on license check failure */ }
        // ────────────────────────────────────────────────────────────────────

        completeLogin(data.access_token, data.user_summary, rememberMe);
        navigate('/select-app');
      }
    } catch (err) {
      const errData = err.response?.data;
      const status = err.response?.status;
      // 21 CFR lockout
      if (status === 423) {
        setLockoutMsg(errData);
        toast.error(errData?.error || 'Account locked.');
      } else if (status === 503) {
        toast.warning('Server is still starting up. Please wait 30–60 seconds and try again.', { duration: 6000 });
      } else {
        toast.error(errData?.error || errData?.message || 'Login failed. Please check your credentials.');
      }
    } finally { setLoading(false); }
  }, [username, subUsername, password, rememberMe, geoCoords, loginWithTemp, completeLogin, navigate, addRecent]);

  // CompanySelector + handleCompanySelect removed — login no longer requires picking a company.

  // Derived insight buckets
  const tasks    = userInsights.filter(i => i.category === 'task').slice(0, 3);
  const alerts   = userInsights.filter(i => i.category === 'alert').slice(0, 2);
  const messages = userInsights.filter(i => i.category === 'message').slice(0, 2);

  return (
    <div className="min-h-screen relative overflow-hidden dark:bg-[#080808] bg-white pb-10" data-testid="login-page">
      {isDark && <FloatingParticles />}
      <div className="absolute inset-0 pointer-events-none"
        style={{ background: isDark
          ? 'radial-gradient(1200px circle at 50% 42%, rgba(61,213,109,0.04) 0%, transparent 60%)'
          : 'radial-gradient(1000px circle at 50% 42%, rgba(61,213,109,0.04) 0%, transparent 55%)' }} />

      {/* Theme toggle */}
      <div className="absolute top-3 right-3 z-50 flex items-center gap-2">
        <LanguageSwitcher data-testid="login-language-switcher" theme={isDark ? 'dark' : 'light'} />
        <ThemeToggle />
      </div>

      {/* ── GRID ── */}
      <div className="relative z-10 min-h-screen grid grid-cols-12">

        {/* ════════════════════════════════════════════════════
            LEFT  — hidden on mobile/tablet, visible desktop
            ════════════════════════════════════════════════════ */}
        <div className="hidden lg:flex lg:col-span-3 flex-col pt-4 pr-2 pl-4">
          <div className="mb-3"><RetailCliqLogo size="xs" showTagline /></div>

          {/* Tech Partner */}
          <div className="flex items-center gap-1.5 mb-3">
            <div className="flex items-center gap-1 px-2 py-0.5 rounded-full dark:bg-[#3DD56D]/10 bg-[#3DD56D]/8 border dark:border-[#3DD56D]/20 border-[#3DD56D]/20">
              <BadgeCheck size={11} className="text-[#3DD56D]" />
              <span className="text-[10px] font-semibold text-[#3DD56D]">Technology Partner</span>
            </div>
          </div>

          {/* Company info */}
          <div className="mb-3">
            <SL icon={Building2} text="Company Info" />
            <div className="space-y-1">
              {[['Organization','RetailCliQ Corp'],['Plan','Enterprise'],['Users','10 / 10'],['Valid','Dec 2025'],['Support','24×7']].map(([l,v]) => (
                <div key={l} className="flex items-center justify-between">
                  <span className="dark:text-neutral-500 text-gray-400 text-[10px]">{l}</span>
                  <span className="dark:text-neutral-200 text-gray-700 text-[10px] font-medium">{v}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Platform stats */}
          <div className="mb-3">
            <SL icon={Cpu} text="Platform" />
            <div className="grid grid-cols-2 gap-1">
              {[['2500+','Stores'],['50+','Modules'],['99.9%','Uptime'],['v2.4','Version']].map(([val,lbl],i) => (
                <div key={i} className="py-1">
                  <p className="text-sm font-bold text-[#3DD56D] tabular-nums leading-none" style={{ fontFamily:"'IBM Plex Mono',monospace" }}>{val}</p>
                  <p className="dark:text-neutral-600 text-gray-400 text-[10px]">{lbl}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Certifications */}
          <div className="mb-3">
            <div className="flex flex-wrap gap-1">
              {['ISO 27001','PCI-DSS','GST','MFA','SSO'].map(c => (
                <span key={c} className="text-[9px] dark:bg-white/[0.04] bg-gray-100 dark:border-white/[0.07] border-gray-200 border px-1.5 py-0.5 rounded-full dark:text-neutral-400 text-gray-500">{c}</span>
              ))}
            </div>
          </div>

          {/* Product news */}
          <div className="flex-1">
            <SL icon={Globe2} text="Product News" />
            <div className="space-y-1.5">
              {compInsights.slice(0,4).map((ins,i) => (
                <div key={ins.id||i} className="flex items-start gap-1.5">
                  <div className="w-1.5 h-1.5 rounded-full mt-1 flex-shrink-0" style={{ backgroundColor: CAT_META[ins.category] || '#3DD56D' }} />
                  <div className="min-w-0">
                    <p className="dark:text-neutral-200 text-gray-700 text-[11px] font-medium truncate">{ins.title}</p>
                    {ins.body && <p className="dark:text-neutral-500 text-gray-400 text-[10px] rcq-line-clamp-1">{ins.body}</p>}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="pt-2 mt-2 dark:border-t dark:border-white/[0.05] border-t border-gray-100">
            <p className="dark:text-neutral-700 text-gray-300 text-[10px]">© 2025 RetailCliQ Corp.</p>
          </div>
        </div>

        {/* ════════════════════════════════════════════════════
            CENTER — full mobile, 8col tablet, 6col desktop
            ════════════════════════════════════════════════════ */}
        <div className="col-span-12 md:col-span-8 lg:col-span-6 flex flex-col items-center pt-3 px-3 sm:px-4 lg:px-5">

          {/* Mobile logo */}
          <div className="flex lg:hidden justify-center mb-3 w-full">
            <RetailCliqLogo size="sm" showTagline />
          </div>

          <motion.div
            initial={{ opacity:0, y:14 }} animate={{ opacity:1, y:0 }}
            transition={{ duration:0.38, ease:'easeOut' }}
            className="w-full max-w-[400px]"
          >
            {/* CompanySelector removed: login no longer asks the user to pick a company. */}

            {/* ── LOGIN CARD (HIGHLIGHTED) ───────────────────────────────── */}
            <div className={[
              'rounded-2xl p-4 sm:p-5',
              'bg-white border border-gray-200 shadow-xl',
              'dark:bg-white/[0.05] dark:border rcq-login-card',
            ].join(' ')}>

              <div className="mb-4">
                <h2 className="text-lg font-bold dark:text-white text-gray-900" style={{ fontFamily:"'Space Grotesk',sans-serif" }}>{t('welcomeBack')}</h2>
                <p className="dark:text-neutral-400 text-gray-500 text-xs mt-0.5">{t('signInToAccount')}</p>
              </div>

              {/* 21 CFR lockout alert */}
              {lockoutMsg && (
                <div className="mb-3 dark:bg-[#FF5A6A]/[0.08] bg-red-50 border dark:border-[#FF5A6A]/25 border-red-200 rounded-xl px-3 py-2.5">
                  <p className="text-xs text-[#FF5A6A] font-medium mb-0.5">🔒 Account Locked</p>
                  <p className="text-[11px] dark:text-neutral-300 text-gray-600">{lockoutMsg.error}</p>
                  {lockoutMsg.minutesRemaining > 0 && (
                    <p className="text-[10px] dark:text-neutral-500 text-gray-400 mt-0.5">Auto-unlocks in {lockoutMsg.minutesRemaining} minute(s).</p>
                  )}
                  {lockoutMsg.adminLock && (
                    <p className="text-[10px] text-[#F5C451] mt-0.5">⚠ Contact your administrator to unlock.</p>
                  )}
                </div>
              )}

              {/* 21 CFR expiry warning */}
              {expiryWarn && (
                <div className="mb-3 dark:bg-[#F5C451]/[0.08] bg-yellow-50 border dark:border-[#F5C451]/25 border-yellow-200 rounded-xl px-3 py-2.5">
                  <p className="text-xs text-[#F5C451] font-medium mb-0.5">⚠ Password Expiry Warning</p>
                  <p className="text-[11px] dark:text-neutral-300 text-gray-600">Your password expires in <strong>{expiryWarn.expiresInDays}</strong> day(s).</p>
                  <button onClick={() => navigate('/change-password', { state:{ reason:'voluntary' } })} className="text-[11px] text-[#3DD56D] hover:text-[#5fe88a] mt-0.5 font-medium">Change password now →</button>
                </div>
              )}

              {/* Auth tabs */}
              <Tabs value={authTab} onValueChange={setAuthTab} className="mb-0.5" data-testid="auth-method-tabs">
                <TabsList className="w-full dark:bg-white/[0.05] bg-gray-100 dark:border-white/[0.08] border-gray-200 border p-0.5 rounded-xl h-auto gap-0.5">
                  <TabsTrigger value="password" className="flex-1 text-[11px] py-1.5 rounded-lg dark:data-[state=active]:bg-white/[0.13] data-[state=active]:bg-white dark:data-[state=active]:text-white data-[state=active]:text-gray-900 dark:text-neutral-400 text-gray-500 transition-all">Password</TabsTrigger>
                  {[{v:'voice',l:'Voice',enabled:true},{v:'biometric',l:'Biometric',enabled:false},{v:'face',l:'Face',enabled:false}].map(({v,l,enabled}) => (
                    <TabsTrigger key={v} value={v} disabled={!enabled} data-testid={`auth-tab-${v}`} className={`flex-1 text-[11px] py-1.5 rounded-lg ${enabled ? 'dark:data-[state=active]:bg-white/[0.13] data-[state=active]:bg-white dark:data-[state=active]:text-white data-[state=active]:text-gray-900 dark:text-neutral-400 text-gray-500' : 'dark:text-neutral-600 text-gray-300 cursor-not-allowed'} transition-all`}>{l}</TabsTrigger>
                  ))}
                </TabsList>

                <TabsContent value="password" className="mt-3" data-dev-component="LoginPortal">
                  <FormHelp formName="Login">
                  <form onSubmit={handleLogin} className="space-y-2.5">
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 dark:text-neutral-500 text-gray-400" size={13} />
                      <Input data-testid="login-username-input" data-help-control="username" type="text" placeholder={t('usernamePlaceholder')} value={username} onChange={e => setUsername(e.target.value)} autoComplete="username"
                        className="pl-8 h-9 rounded-xl text-sm dark:bg-white/[0.05] bg-gray-50 dark:border-white/[0.09] border-gray-200 dark:text-white text-gray-900 dark:placeholder:text-neutral-500 placeholder:text-gray-400 focus-visible:ring-[#3DD56D] focus-visible:ring-1" />
                    </div>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 dark:text-neutral-500 text-gray-400" size={13} />
                      <Input data-testid="login-password-input" data-help-control="password" type={showPwd ? 'text' : 'password'} placeholder={t('passwordPlaceholder')} value={password} onChange={e => setPassword(e.target.value)} autoComplete="current-password"
                        className="pl-8 pr-8 h-9 rounded-xl text-sm dark:bg-white/[0.05] bg-gray-50 dark:border-white/[0.09] border-gray-200 dark:text-white text-gray-900 dark:placeholder:text-neutral-500 placeholder:text-gray-400 focus-visible:ring-[#3DD56D] focus-visible:ring-1" />
                      <button type="button" onClick={() => setShowPwd(v => !v)} className="absolute right-2.5 top-1/2 -translate-y-1/2 dark:text-neutral-500 text-gray-400 dark:hover:text-neutral-300 hover:text-gray-600 transition-colors">
                        {showPwd ? <EyeOff size={13}/> : <Eye size={13}/>}
                      </button>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1.5">
                        <Checkbox id="rm" data-help-control="remember_me" checked={rememberMe} onCheckedChange={v => setRememberMe(!!v)} className="h-3.5 w-3.5 dark:border-white/[0.2] border-gray-300 data-[state=checked]:bg-[#3DD56D] data-[state=checked]:border-[#3DD56D]" />
                        <label htmlFor="rm" className="text-xs dark:text-neutral-400 text-gray-500 cursor-pointer select-none">{t('rememberMe')}</label>
                      </div>
                      <button type="button" data-testid="login-forgot-password-link" onClick={() => setShowForgot(true)} className="text-xs text-[#3DD56D] hover:text-[#5fe88a] transition-colors font-medium">{t('forgotPassword')}</button>
                    </div>
                    <Button type="submit" data-testid="login-submit-button" disabled={loading}
                      className="w-full h-9 rounded-xl bg-[#3DD56D] hover:bg-[#4ce07c] text-black font-semibold shadow-[0_4px_18px_rgba(61,213,109,0.26)] disabled:opacity-50 transition-colors text-sm">
                      {loading
                        ? <span className="flex items-center gap-1.5"><span className="w-3.5 h-3.5 border-2 border-black/20 border-t-black rounded-full rcq-spin" />{t('signingIn')}</span>
                        : <span className="flex items-center gap-1.5">{t('signInBtn')} <ArrowRight size={14}/></span>}
                    </Button>
                  </form>
                  </FormHelp>

                  {/* Quick Login — only previously-used accounts on this device */}
                  {recent.length > 0 && (
                    <div className="mt-3">
                      <p className="text-[10px] dark:text-neutral-500 text-gray-400 mb-1.5 flex items-center gap-1">
                        <CheckCircle size={9} className="text-[#3DD56D]" /> Quick Login <span className="dark:text-neutral-700 text-gray-300">(recent)</span>
                      </p>
                      <div className="flex gap-1.5 flex-wrap">
                        {recent.slice(0, 4).map(c => (
                          <button key={c.client_id} type="button"
                            data-testid={`quick-login-${c.client_id}`}
                            onClick={() => { setUsername(c.user_name || ''); setPassword(''); }}
                            title={c.user_name ? `Sign in as ${c.user_name}` : 'Prefill username'}
                            className="flex items-center gap-1.5 px-2 py-1 rounded-lg dark:bg-white/[0.04] bg-gray-50 dark:border-white/[0.07] border-gray-200 border dark:hover:bg-white/[0.08] hover:bg-gray-100 transition-colors">
                            <div className="w-4 h-4 rounded-md bg-[#3DD56D] flex items-center justify-center flex-shrink-0">
                              <span className="text-black font-bold text-[8px]">
                                {(c.company || c.name || c.user_name || c.client_id).charAt(0).toUpperCase()}
                              </span>
                            </div>
                            <div className="text-left min-w-0">
                              <p className="text-[10px] dark:text-neutral-200 text-gray-700 font-medium truncate max-w-[100px]">{c.user_name || c.name || '—'}</p>
                              <p className="text-[9px] dark:text-neutral-600 text-gray-400 truncate max-w-[100px]">{c.company || c.client_id}</p>
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="voice" className="mt-3">
                  <VoiceLoginPanel
                    API_BASE={API.replace(/\/api$/, '')}
                    onSwitchToPassword={(uname) => { if (uname) setUsername(uname); setAuthTab('password'); }}
                    onSuccess={(d) => {
                      try {
                        const storage = rememberMe ? localStorage : sessionStorage;
                        if (d.globalContext) storage.setItem('rcq_global_context', JSON.stringify(d.globalContext));
                      } catch (_) {}
                      completeLogin(d.access_token, d.user_summary, rememberMe);
                      navigate('/select-app');
                    }}
                  />
                </TabsContent>

                {['biometric','face'].map(tab => (
                  <TabsContent key={tab} value={tab} className="mt-3">
                    <div className="flex flex-col items-center py-6 text-center">
                      <div className="w-10 h-10 rounded-xl dark:bg-white/[0.05] bg-gray-100 dark:border-white/[0.1] border-gray-200 border flex items-center justify-center mb-2">
                        {tab==='biometric'&&<Fingerprint className="dark:text-neutral-400 text-gray-400" size={17}/>}
                        {tab==='face'&&<Scan className="dark:text-neutral-400 text-gray-400" size={17}/>}
                      </div>
                      <Badge className="mb-1.5 bg-[#3DD56D]/10 text-[#3DD56D] border-[#3DD56D]/25 border text-[11px]">{t('comingSoon')}</Badge>
                      <p className="dark:text-neutral-400 text-gray-500 text-xs">Coming in next release.</p>
                    </div>
                  </TabsContent>
                ))}
              </Tabs>

              <Separator className="dark:bg-white/[0.07] bg-gray-100 my-3" />
              {/* "Request Access" link hidden per product request — uncomment to re-enable
              <p className="text-center text-xs dark:text-neutral-500 text-gray-500">
                {t('newTo')}{' '}
                <a href="/signup" data-testid="login-signup-link" className="text-[#3DD56D] hover:text-[#5fe88a] transition-colors font-medium">{t('requestAccess')}</a>
              </p>
              */}
            </div>

            {/* ── BELOW CARD: Thought + Idea + Weather ─────────────────── */}
            <motion.div
              initial={{ opacity:0, y:8 }} animate={{ opacity:1, y:0 }}
              transition={{ delay:0.28, duration:0.35 }}
              className="mt-3 space-y-2"
            >
              {/* Today's Thought */}
              <div className="flex items-start gap-2">
                <Quote size={12} className="text-[#3DD56D] opacity-60 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-[9px] font-semibold dark:text-neutral-500 text-gray-400 uppercase tracking-widest mb-0.5">Today's Thought</p>
                  <p className="dark:text-neutral-300 text-gray-600 text-[11px] italic leading-relaxed">"{todayThought.text}"</p>
                  <p className="dark:text-neutral-600 text-gray-400 text-[10px] mt-0.5">— {todayThought.author}</p>
                </div>
              </div>

              {/* Idea */}
              <div className="flex items-start gap-2">
                <Lightbulb size={12} className="text-[#F5C451] opacity-70 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-[9px] font-semibold dark:text-neutral-500 text-gray-400 uppercase tracking-widest mb-0.5">
                    New Idea · <span className="normal-case dark:text-neutral-600 text-gray-400">{todayIdea.category}</span>
                  </p>
                  <p className="dark:text-neutral-300 text-gray-600 text-[11px] leading-relaxed">{todayIdea.text}</p>
                </div>
              </div>

              {/* Weather + Date */}
              <div className="flex items-center gap-3 flex-wrap">
                <WeatherWidget compact />
                <div className="flex items-center gap-1 flex-shrink-0">
                  <Calendar size={10} className="dark:text-neutral-500 text-gray-400" />
                  <span className="dark:text-neutral-400 text-gray-500 text-[11px]">{formatDate(time,{weekday:'short'})}</span>
                  <span className="dark:text-neutral-600 text-gray-300 text-[11px]">·</span>
                  <span className="dark:text-neutral-400 text-gray-500 text-[11px] tabular-nums" style={{ fontFamily:"'IBM Plex Mono',monospace" }}>{formatTime(time)}</span>
                </div>
              </div>
            </motion.div>

            {/* Model links (small) */}
            <div className="flex items-center gap-3 mt-2 justify-center">
              {[{l:'v2',h:'/login/v2'},{l:'v3',h:'/login/v3'},{l:'Jewellery',h:'/login/industry/jewellery'},{l:'Apparel',h:'/login/industry/apparel'}].map(({l,h}) => (
                <a key={l} href={h} className="text-[10px] dark:text-neutral-700 text-gray-300 dark:hover:text-[#3DD56D] hover:text-[#3DD56D] transition-colors">{l}</a>
              ))}
            </div>
          </motion.div>
        </div>

        {/* ════════════════════════════════════════════════════
            RIGHT — hidden mobile, 4col tablet, 3col desktop
            ════════════════════════════════════════════════════ */}
        <div className="hidden md:flex md:col-span-4 lg:col-span-3 flex-col pt-3 px-3 lg:pl-2 lg:pr-4">

          {/* User insights header */}
          <div className="mb-2.5">
            <SL icon={Star} text="User Insights" color="#F5C451" />
            <p className="dark:text-neutral-400 text-gray-600 text-[11px]">Sign in to see personalized data.</p>
          </div>

          {/* Last Activity */}
          <div className="mb-2.5">
            <SL icon={Activity} text="Last Activity" />
            <div className="space-y-1">
              {activity.map((a,i) => (
                <div key={i} className="flex items-start gap-1.5">
                  <div className="w-1 h-1 rounded-full bg-[#3DD56D]/60 mt-1.5 flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="dark:text-neutral-200 text-gray-700 text-[11px] truncate">{a.action}</p>
                    <p className="dark:text-neutral-600 text-gray-400 text-[10px]">{a.time} · {a.detail}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Tasks */}
          {tasks.length > 0 && (
            <div className="mb-2.5">
              <SL icon={CheckSquare} text="Pending Tasks" color="#FF5A6A" />
              <div className="divide-y dark:divide-white/[0.04] divide-gray-50">
                {tasks.map((item,i) => <InsightRow key={i} item={item} />)}
              </div>
            </div>
          )}

          {/* Alerts */}
          {alerts.length > 0 && (
            <div className="mb-2.5">
              <SL icon={AlertTriangle} text="Alerts" color="#F5C451" />
              <div className="divide-y dark:divide-white/[0.04] divide-gray-50">
                {alerts.map((item,i) => <InsightRow key={i} item={item} />)}
              </div>
            </div>
          )}

          {/* Messages */}
          {messages.length > 0 && (
            <div className="mb-2.5 flex-1">
              <SL icon={MessageCircle} text="Messages" color="#4CC3FF" />
              <div className="divide-y dark:divide-white/[0.04] divide-gray-50">
                {messages.map((item,i) => <InsightRow key={i} item={item} />)}
              </div>
            </div>
          )}

          <div className="pt-2 mt-auto dark:border-t dark:border-white/[0.05] border-t border-gray-100">
            <div className="flex items-center gap-1 mb-1">
              <Shield size={9} className="text-[#3DD56D]" />
              <p className="dark:text-neutral-600 text-gray-400 text-[10px]">MFA · SSO · ISO 27001</p>
            </div>
          </div>
        </div>

      </div>
      {showForgot && <ForgotPasswordModal onClose={() => setShowForgot(false)} />}
    </div>
  );
}
